<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip content="イベント実装作成" placement="bottom" v-if="isEditable" :open-delay="500">
        <v-btn icon :disabled="!processImplementation" @click="openCreateDialog">
          <v-icon>mdi-lightning-bolt-circle</v-icon>
        </v-btn>
      </el-tooltip>
    </template>

    <el-table
      :data="eventImplementationsView"
      row-key="id"
      :height="tableHeight"
      :default-sort="{prop: 'name', order: 'ascending'}">

      <el-table-column
          label="名称"
          prop="localId"
          width="200"
          sortable>
        <template #header>
          名称
          <el-tooltip content="イベント実装の名称／内部ID" placement="top" :open-delay="500">
            <v-icon small color="blue">mdi-information</v-icon>
          </el-tooltip>
        </template>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name"
                :value-local-id="row.localId"
                placeholder="名称未設定">
              <template #prepend>
                <el-tooltip content="イベント" :open-delay="500" placement="left">
                  <v-icon>mdi-lightning-bolt-circle</v-icon>
                </el-tooltip>
              </template>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          label="説明"
          prop="description">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="60"
          align="center"
          prop="isMonitored"
          label="記録">
        <template #default="{row}">
          <readonly-checkbox :value="row.isMonitored"></readonly-checkbox>
        </template>
      </el-table-column>

      <el-table-column
          label="記録内容"
          prop="monitoringContent">
          <template #default="{row}">
            <table-inline>
              <text-field-like
                  :value="row.isMonitored ? row.monitoringContent : null"
                  :placeholder="row.isMonitored ? '未設定' : '\u2E3B'"
                  multi-line>
              </text-field-like>
            </table-inline>
          </template>
      </el-table-column>

      <el-table-column
          width="95"
          align="right"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <!-- 編集モード -->
            <template v-if="isEditable">
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>
              <icon-btn
                  type="edit-content"
                  @click="openUpdateDialog(row)">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>

      <template #empty>
        <v-row
            v-if="eventImplementationsLoading"
            justify="center"
            class="pa-10">
          <v-progress-circular indeterminate/>
        </v-row>
        <v-row
            v-else-if="processImplementation == null"
            justify="center"
            class="pa-10">
          プロセス実装を選択してください
        </v-row>
      </template>
    </el-table>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate.visible"
        title-text="イベント実装作成"
        @close="closeCreateDialog"
        @apply="createEventImplementation">
      <event-implementation-create-form
          v-model="dialogCreate.form"
          :process-implementation="processImplementation"
          :event-implementations="eventImplementationsView">
      </event-implementation-create-form>
    </simple-dialog>

    <!-- 編集ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate.visible"
        title-text="イベント実装編集"
        @close="closeUpdateDialog"
        @apply="updateEventImplementation">
      <event-implementation-create-form
          v-model="dialogUpdate.form"
          :process-implementation="processImplementation"
          :event-implementations="eventImplementationsView">
      </event-implementation-create-form>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        title-text="イベント実装削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のイベント実装を削除します。本当によろしいですか？"
        @close="closeDeleteDialog"
        @apply="deleteEventImplementation">
      <event-implementation-create-form
          v-model="dialogDelete.form"
          :process-implementation="processImplementation"
          readonly>
      </event-implementation-create-form>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import TableInline from "@/components/atoms/TableInline.vue";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import EventImplementationCreateForm from "@/components/pages/implementation/ServiceImplementationShow/EventImplementationCreateForm.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import { v4 as uuidv4 } from "uuid";
import {doLayoutElTable} from "@/utils/DomUtil";
import Formatter from "@/utils/Formatter";

export default {
  name: 'EventImplementationTable',
  components: {
    TableInline,
    IconBtn,
    IconBtns,
    ReadonlyCheckbox,
    TabItemTemplate,
    SimpleDialog,
    EventImplementationCreateForm,
    TextFieldLike
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin,
    Formatter
  ],
  props: {
    processImplementation: {
      type: Object,
      default: null
    },
    height: {
      type: Number,
    },
  },
  data() {
    return {
      dialogCreate: {
        visible: false,
        form: {}
      },
      dialogUpdate: {
        visible: false,
        form: {}
      },
      dialogDelete: {
        visible: false,
        form: {}
      },
      eventImplementations: [],
      eventImplementationsLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    eventImplementationsView() {
      if (!this.processImplementation) {
        return [];
      }
      return this.eventImplementations
        .filter(ei => {
          return ei.processImplementationId === this.processImplementation.id
        })
    },
    tableHeight() {
      return this.height ? Math.max(0, this.height - 48) : undefined;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    openCreateDialog() {
      this.dialogCreate = {
        visible: true,
        form: {
          ...this.defaultEventImplementationForm(),
          localId: uuidv4(),
          processImplementationId: this.processImplementation.id,
        }
      };
    },
    closeCreateDialog() {
      this.dialogCreate.visible = false;
    },
    openUpdateDialog(eventImplementation) {
      this.dialogUpdate = {
        visible: true,
        form: {
          ...eventImplementation
        }
      };
    },
    closeUpdateDialog() {
      this.dialogUpdate.visible = false;
    },
    openDeleteDialog(eventImplementation) {
      this.dialogDelete = {
        visible: true,
        form: {
          ...eventImplementation
        }
      }
    },
    closeDeleteDialog() {
      this.dialogDelete.visible = false;
    },
    defaultEventImplementationForm() {
      return {
        name: this.processImplementation.name,
        localId: null,
        description: null,
        monitoringContent: null,
        isMonitored: true,
      }
    },
    createEventImplementation() {
      this.showSnackbar({
        message: 'イベント実装を登録中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/eventImplementation/create', this.dialogCreate.form)
        .then(ei => {
          this.$store.commit({
            type: 'showSnackbar',
            message: `イベント実装が登録されました`
          });

          this.eventImplementations.push(ei);
          this.$emit('update-count', this.eventImplementations.length);
        })
        .catch(() => {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `イベント実装の登録に失敗しました`
          });
        })
    },
    updateEventImplementation() {
      this.showSnackbar({
        message: 'イベント実装を更新中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/eventImplementation/update', this.dialogUpdate.form)
        .then(() => {
          const ei = this.eventImplementations.find(ei => ei.id === this.dialogUpdate.form.id);
          if(ei) {
            Object.entries(this.dialogUpdate.form).forEach(([key, value]) => {
              this.$set(ei, key, value)
            });
          }
          this.$store.commit({
            type: 'showSnackbar',
            message: `イベント実装が更新されました`
          });
        })
        .catch(e => {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: this.formatError(e, 'イベント実装の更新に失敗しました')
          });
        })
    },
    deleteEventImplementation() {
      const pos = this.eventImplementations.findIndex(ei => ei.id === this.dialogDelete.form.id)
      if(pos !== -1) {
        this.eventImplementations.splice(pos, 1);
        this.$emit('update-count', this.eventImplementations.length);
      }

      this.showSnackbar({
        message: 'イベント実装を削除中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/eventImplementation/delete', this.dialogDelete.form.id)
        .then(() => {
          this.$store.commit({
            type: 'showSnackbar',
            message: `イベント実装が削除されました`
          });
        })
        .catch(() => {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `イベント実装の削除に失敗しました`
          });
        })
    }
  },
  watch: {
    processImplementation: {
      immediate: true,
      async handler(selectedProcessImplementation) {
        if(!selectedProcessImplementation) {
          return;
        }

        try {
          this.eventImplementations = [];
          this.eventImplementationsLoading = true;

          const {data: eventImplementations} = await this.hcsClient.get('/event_implementations', {
            params: {
              processImplementationIdEq: selectedProcessImplementation.id
            }
          });
          this.eventImplementations = eventImplementations;
        } finally {
          this.eventImplementationsLoading = false;
          doLayoutElTable(this.$el);
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.multipane.layout-v .multipane-resizer::after {
  content: "";
  position: relative;
  left: 4px;
  background-color: #ebeef5;
  display: block;
  width: 1px;
  height: 100%;
}
</style>
